"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";

export default function HeroSection() {
  const pathRef = useRef<SVGPathElement>(null);

  useEffect(() => {
    const el = pathRef?.current;
    if (!el) return;
    const len = el?.getTotalLength();
    el.style.strokeDasharray = `${len}`;
    el.style.strokeDashoffset = `${len}`;
    setTimeout(() => {
      el.style.transition = "stroke-dashoffset 2s cubic-bezier(0.16, 1, 0.3, 1)";
      el.style.strokeDashoffset = "0";
    }, 300);
  }, []);

  return (
    <section
      className="min-h-[calc(100vh-5.5rem)] flex items-center relative overflow-hidden"
      style={{ backgroundColor: "#F6F5F2" }}>

      {/* Background grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E")`
        }} />

      <div className="max-w-container mx-auto px-6 lg:px-8 py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Left: Content */}
          <div>
            {/* Section label */}
            <div
              className="section-label mb-8"
              style={{
                opacity: 0,
                animation: "fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.1s forwards"
              }}>

              Premium Kratom Powder — 1 Kilo
            </div>

            {/* Headline */}
            <h1
              className="font-serif font-bold text-charcoal mb-6 leading-none"
              style={{
                fontFamily: "Fraunces, Georgia, serif",
                fontSize: "clamp(2.75rem, 5.5vw, 5rem)",
                letterSpacing: "-0.03em",
                lineHeight: "1.0",
                opacity: 0,
                animation: "fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards"
              }}>

              Stop Guessing.
              <br />
              <span style={{ color: "#3F5A45" }}>Start With a</span>
              <br />
              Kilo You'll Keep.
            </h1>

            {/* Subheadline */}
            <p
              className="text-lg leading-relaxed mb-10 max-w-lg"
              style={{
                color: "#6B6B6B",
                fontFamily: "Inter, system-ui, sans-serif",
                opacity: 0,
                animation: "fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.35s forwards"
              }}>

              One kilo. Three lines. Lab-verified. Built for people who are done rotating between inconsistent batches.
            </p>

            {/* CTAs */}
            <div
              className="flex flex-wrap gap-4 mb-10"
              style={{
                opacity: 0,
                animation: "fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.5s forwards"
              }}>

              <Link href="/shop" className="btn-primary">
                Choose Your Line
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M13 6L19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
              <Link href="/lab-testing" className="btn-secondary">
                View Lab Results
              </Link>
            </div>

            {/* Trust row */}
            <div
              className="flex flex-wrap items-center gap-x-5 gap-y-2"
              style={{
                opacity: 0,
                animation: "fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.65s forwards"
              }}>

              {[
              { icon: "🧪", label: "Lab Tested" },
              { icon: "📄", label: "Public COAs" },
              { icon: "🎁", label: "Free Sample" },
              { icon: "✅", label: "30-Day Guarantee" }]?.
              map((item, i) =>
              <span key={i} className="trust-item">
                  <span>{item?.icon}</span>
                  {item?.label}
                  {i < 3 && <span className="text-border ml-2">·</span>}
                </span>
              )}
            </div>
          </div>

          {/* Right: Animated K Logo */}
          <div
            className="flex items-center justify-center"
            style={{
              opacity: 0,
              animation: "scaleIn 1s cubic-bezier(0.16, 1, 0.3, 1) 0.4s forwards"
            }}>

            <div
              className="relative"
              style={{ animation: "floatY 7s ease-in-out infinite" }}>

              {/* Outer glow ring */}
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: "radial-gradient(circle, rgba(63,90,69,0.08) 0%, transparent 70%)",
                  transform: "scale(1.4)"
                }} />


              {/* Logo Image */}
              <div
                className="relative w-72 h-72 lg:w-96 lg:h-96 rounded-full flex items-center justify-center overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, #F6F5F2 0%, #EDECEA 100%)",
                  boxShadow: "0 32px 80px rgba(63,90,69,0.15), 0 0 0 1px rgba(63,90,69,0.1)"
                }}>

                {/* Inner decorative ring */}
                <div
                  className="absolute inset-6 rounded-full"
                  style={{
                    border: "1px solid rgba(63,90,69,0.12)"
                  }} />


                <img
                  src="/assets/images/kratapillar_logofinal.pdf-1772558267829.png"
                  alt="Kratapillar logo"
                  className="relative z-10 w-64 h-64 lg:w-80 lg:h-80 object-contain" />

              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}